import Color from "color";

import type { DefaultTheme } from "simplydo/react/styled";

import type { colorMap } from "../../theme/constants";
import type { UiColorPalette } from "./UIContext";
import { textColorsFor } from "./textColors";

export type UiPaletteKey =
  | keyof typeof colorMap
  | "primary"
  | "secondary"
  | "onPrimary"
  | "onSecondary";

export type UiPalettes = Record<UiPaletteKey, UiColorPalette>;

function paletteFromFill(fill: string, colorKey: string): UiColorPalette {
  const { colorInstance, textOnWhite, textOnSolid } = textColorsFor(
    fill,
    colorKey,
  );
  return {
    bg: fill,
    bgHover: colorInstance.darken(0.1).string(),
    bgActive: colorInstance.darken(0.2).string(),
    bgSoft: colorInstance.lighten(0.2).string(),
    fg: textOnSolid,
    border: colorInstance.string(),
    borderHover: colorInstance.darken(0.3).string(),
    fgOnLight: textOnWhite,
  };
}

/** White/black contrast chrome for use on primary/secondary fills. */
function buildMonoPalette(fill: "#fff" | "#000"): UiColorPalette {
  const colorInstance = Color(fill);
  const isWhite = fill === "#fff";
  const contrast = isWhite ? "#000" : "#fff";

  return {
    bg: fill,
    bgHover: colorInstance.alpha(0.1).string(),
    bgActive: colorInstance.alpha(0.2).string(),
    bgSoft: colorInstance.alpha(0.3).string(),
    fg: contrast,
    border: fill,
    borderHover: colorInstance.alpha(0.5).string(),
    fgOnLight: fill,
  };
}

export type BuildUiPalettesInput = {
  colorMap: typeof colorMap;
  primaryColour: string;
  secondaryColour: string;
  shouldBeWhiteOnPrimary: boolean;
  shouldBeWhiteOnSecondary: boolean;
};

/** Precompute every known UI colour palette (call once when building the theme). */
export function buildUiPalettes(input: BuildUiPalettesInput): UiPalettes {
  const palettes = {} as UiPalettes;

  for (const key of Object.keys(input.colorMap) as (keyof typeof colorMap)[]) {
    palettes[key] = paletteFromFill(input.colorMap[key], key);
  }

  palettes.primary = paletteFromFill(input.primaryColour, "primary");
  palettes.secondary = paletteFromFill(input.secondaryColour, "secondary");
  palettes.onPrimary = buildMonoPalette(
    input.shouldBeWhiteOnPrimary ? "#fff" : "#000",
  );
  palettes.onSecondary = buildMonoPalette(
    input.shouldBeWhiteOnSecondary ? "#fff" : "#000",
  );

  return palettes;
}

/** Look up a precomputed palette from the theme. */
export function resolveUiPalette(
  theme: DefaultTheme,
  color: UiPaletteKey,
): UiColorPalette {
  const palette = theme.colors?.[color];
  if (!palette) {
    throw new Error(`Unknown UI color: ${String(color)}`);
  }
  return palette;
}
